import * as React from "react";
import { AccountMenu } from "../../AccountMenu";
import {
  LoaderFunction,
  LoaderFunctionArgs,
  Outlet,
  redirect,
} from "react-router-dom";
import { Box, Drawer, Toolbar, useMediaQuery, useTheme } from "@mui/material";
import { AccountHolder, useAccount } from "../../Account";

// https://github.com/remix-run/react-router/discussions/9792#discussioncomment-4809811
export type LoaderData<TLoaderFn extends LoaderFunction> = Awaited<
  ReturnType<TLoaderFn>
> extends Response | infer D
  ? D
  : never;

export async function myAccountLoader({ request }: LoaderFunctionArgs) {
  console.log("myAccountLoader executing");

  const { user, isAuthenticated, patron, accountSummary } =
    await AccountHolder.fetchAccountData();
  
  const {pathname: requestedPath, hash, search} = new URL(request.url)
  
  if (!isAuthenticated) {
    let params = new URLSearchParams(search.slice(1)); //drop the question mark symbol

    params.set("from", requestedPath);
    return redirect("/login?" + params.toString());
  }

  const patronOnlyPaths = [
    "/myaccount/summary",
    "/myaccount/edit-pin",
    "/myaccount/edit-password",
    "/myaccount/edit-username",
    "/myaccount/create-username",
    "/myaccount/edit-contact-info",
    "/myaccount/linked",
    "/myaccount/loans",
    "/myaccount/holds",
    "/myaccount/fines",
    "/myaccount/history",
  ];

  const configAdminPaths = [
    "/config/branding",
    "/config/landing-page",
    "/config/tags",
    "/config/logo-upload",
    "/config"
  ];

  if (!patron) {
    if (patronOnlyPaths.includes(requestedPath)) {
      console.log("Patron only route, redirecting");
      return redirect("/config/branding");
    }
  }

  const {
    topRole: { authority },
  } = user;

  if (
    configAdminPaths.includes(requestedPath) &&
    authority !== "ROLE_CONFIG_ADMIN" &&
    authority !== "ROLE_LIB_ADMIN" &&
    authority !== "ROLE_TLC_ADMIN"
  ) {
    console.warn(`403 Forbidden: ${requestedPath}`);
    return redirect("/myaccount/summary");
  }

  return {
    isAuthenticated,
    user,
    patron,
    accountSummary,
  };
}

export const drawerWidth = "320px";

export const MyAccount: React.FC = () => {
  // using Context instead of LoaderData but fetching data for context in loader...
  const { user, patron, accountSummary } = useAccount();

  const theme = useTheme();
  const hideDrawer = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <Box sx={{ display: "flex" }}>
        {!hideDrawer && (
          <Box
            sx={{
              minHeight: "100vh",
              position: "sticky",
              top: "0",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              borderRight: `1px solid ${theme.palette.divider}`,
              width: drawerWidth,
              flexShrink: 0,
              maxHeight: "100vh", // Constrain the height
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
              },
            }}
          >
            {
              <AccountMenu
                user={user}
                patron={patron}
                accountSummary={accountSummary}
              ></AccountMenu>
            }
          </Box>
        )}
        <Box
          component="main"
          sx={{ flexGrow: 1, bgcolor: "background.default", p: 3 }}
        >
          <Outlet></Outlet>
        </Box>
      </Box>
    </>
  );
};
